import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAgentAuthorization } from '../../hooks/useAgentAuthorization';

/**
 * Authorization guard component for agent management routes
 * Only allows access to users with appropriate permissions
 */
const AgentManagementAuthGuard = ({ children }) => {
  const { currentUser, isLoading } = useAuth();
  const { canManageAgents, userRole, userTenant } = useAgentAuthorization();
  const location = useLocation();

  // Show loading spinner while authentication state is resolving
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  // Check if user has permission to access agent management
  if (!canManageAgents()) {
    console.log('[AgentManagementAuthGuard] Access denied:', { userRole, userTenant, currentUser: currentUser?.uid });
    
    return (
      <div className="bg-red-50 p-6 rounded-lg shadow-md max-w-2xl mx-auto mt-12">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <svg
              className="h-8 w-8 text-red-400"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-lg font-medium text-red-800">Access Denied</h3>
            <div className="mt-2 text-sm text-red-700">
              <p>
                You do not have permission to access this page. Your account may not be set up yet.
              </p>
              {process.env.NODE_ENV === 'development' && (
                <p className="mt-2 font-mono text-xs">
                  Debug: Role={userRole || 'none'}, User={currentUser?.uid}
                </p>
              )}
            </div>
            <div className="mt-4">
              <div className="-mx-2 -my-1.5 flex">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="ml-3 bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  Go Back
                </button>
                <button
                  type="button"
                  onClick={() => (window.location.href = '/')}
                  className="ml-3 bg-red-50 px-2 py-1.5 rounded-md text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                >
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // User has permission, render children
  return children;
};

AgentManagementAuthGuard.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AgentManagementAuthGuard;
